import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import {
  FlowPathsSectionTitle,
  type FlowSectionCountKind,
} from '../flow-paths/FlowPathsPanel.parts';

import type { FlowWeightedItemRowItem } from './flow-weighted-item.types';

const listChrome =
  'divide-y divide-border/40 rounded-md border border-border/50 bg-background/30';

export function splitByActiveWeight(items: FlowWeightedItemRowItem[]) {
  return {
    activeItems: items.filter((i) => i.weight > 0),
    inactiveItems: items.filter((i) => i.weight <= 0),
  };
}

export function ItemsSection(params: {
  title: string;
  items: FlowWeightedItemRowItem[];
  emptyText: string;
  children: (item: FlowWeightedItemRowItem) => ReactNode;
  maxHeightClassName: string;
  countKind: FlowSectionCountKind;
  /** Optional column-labels row rendered inside the list (shares the row width). */
  header?: ReactNode;
}) {
  const { title, items, emptyText, children, maxHeightClassName, countKind, header } = params;
  return (
    <div className="space-y-2">
      <FlowPathsSectionTitle title={title} count={items.length} countKind={countKind} />
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className={cn(maxHeightClassName, 'overflow-y-auto pr-1')}>
          {header}
          <div className={listChrome}>{items.map(children)}</div>
        </div>
      )}
    </div>
  );
}

interface SplitSectionsParams {
  split: ReturnType<typeof splitByActiveWeight>;
  activeEmpty: string;
  inactiveEmpty: string;
  activeMaxHeightClassName: string;
  inactiveMaxHeightClassName: string;
  renderRow: (item: FlowWeightedItemRowItem) => ReactNode;
  countKind: FlowSectionCountKind;
  /** Column-labels row for the Active list (overlay/preview mode). */
  activeHeader?: ReactNode;
}

export function WeightedActiveInactiveSections({
  split,
  activeEmpty,
  inactiveEmpty,
  activeMaxHeightClassName,
  inactiveMaxHeightClassName,
  renderRow,
  countKind,
  activeHeader,
}: SplitSectionsParams) {
  return (
    <>
      <ItemsSection
        title="Active"
        items={split.activeItems}
        emptyText={activeEmpty}
        maxHeightClassName={activeMaxHeightClassName}
        countKind={countKind}
        header={activeHeader}
      >
        {renderRow}
      </ItemsSection>
      <ItemsSection
        title="Inactive"
        items={split.inactiveItems}
        emptyText={inactiveEmpty}
        maxHeightClassName={inactiveMaxHeightClassName}
        countKind={countKind}
      >
        {renderRow}
      </ItemsSection>
    </>
  );
}
